import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PurchaseRequestModal from './PurchaseRequestModal';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTranslation } from '../../locales';
import { fetchAssetByName } from '../../config/wilder_requests';
import { buildSocialCarbonIssuanceUrl } from '../../utils/socialCarbonRegistry';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--surface-overlay);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: var(--app-bg-gradient);
  border: 2px solid var(--accent);
  border-radius: 20px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-lg);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: var(--accent-soft);
  border: 2px solid var(--accent);
  color: var(--accent-secondary);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: bold;
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent);
    color: var(--text-on-accent);
    transform: scale(1.1);
  }
`;

const ModalTitle = styled.h2`
  color: var(--text-heading);
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: 600;
  text-shadow: 2px 2px 4px var(--surface-overlay);
`;

const AssetInfo = styled.div`
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  background: var(--surface-glass);
  border-radius: 12px;
  border: 1px solid var(--border-subtle);

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const InfoLabel = styled.span`
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 0.9rem;
  min-width: 120px;
  flex-shrink: 0;
`;

const InfoValue = styled.span`
  color: var(--text-primary);
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
  word-break: break-word;
  line-height: 1.4;

  @media (max-width: 480px) {
    text-align: left;
  }
`;

const InfoValueLink = styled.a`
  color: var(--text-primary);
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
  word-break: break-word;
  line-height: 1.4;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: var(--border-default);
  text-underline-offset: 3px;
  transition: color 0.2s ease, text-decoration-color 0.2s ease;

  &:hover {
    color: var(--accent);
    text-decoration-color: var(--accent);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
    border-radius: 4px;
  }

  @media (max-width: 480px) {
    text-align: left;
  }
`;

const QuantityHighlight = styled.div`
  background: var(--accent-soft);
  border: 2px solid var(--accent);
  border-radius: 15px;
  padding: 1.5rem;
  text-align: center;
  margin: 1.5rem 0;
`;

const QuantityValue = styled.div`
  font-size: 2rem;
  color: var(--accent);
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const QuantityLabel = styled.div`
  color: var(--text-secondary);
  font-size: 1rem;
  opacity: 0.9;
`;

/* CTA principal mantém o gradiente de marca (verde/azul Vert) em ambos os temas. */
const ActionButton = styled.button`
  background: var(--brand-cta-gradient);
  color: var(--accent-secondary);
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: var(--shadow-md);

  &:hover {
    background: var(--brand-cta-gradient-hover);
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: var(--surface-3);
    color: var(--text-muted);
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid var(--accent-soft);
  border-top: 4px solid var(--accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: var(--danger);
  text-align: center;
  padding: 1rem;
  background: rgba(255, 107, 107, 0.12);
  border-radius: 8px;
  border: 1px solid var(--danger);
`;

const AssetDetailsModal = ({ isOpen, onClose, assetName }) => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  
  const [assetDetails, setAssetDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen && assetName) {
      fetchAssetDetails();
    }
  }, [isOpen, assetName]);

  const fetchAssetDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const row = await fetchAssetByName(assetName);
      if (row) {
        setAssetDetails(row);
      } else {
        setError(t.assetModal.noAssetFound);
      }
    } catch (err) {
      console.error('Erro ao buscar Detalhes do Pacote de Créditos:', err);
      setError(err.message || t.assetModal.error);
    } finally {
      setLoading(false);
    }
  };

  const na = () => t.assetModal.notAvailable;

  const formatDate = (dateString) => {
    if (!dateString) return na();
    try {
      const date = new Date(dateString);
      const locale = language === 'pt' ? 'pt-BR' : 'en-US';
      return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return na();
    }
  };

  const formatNumber = (number) => {
    if (!number && number !== 0) return '0';
    const locale = language === 'pt' ? 'pt-BR' : 'en-US';
    return new Intl.NumberFormat(locale).format(number);
  };

  const handlePurchaseRequest = () => {
    setIsPurchaseModalOpen(true);
  };

  const closePurchaseModal = () => {
    setIsPurchaseModalOpen(false);
  };

  const assetRegistryUrl = buildSocialCarbonIssuanceUrl(assetDetails?._id);

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <CloseButton onClick={onClose}>&times;</CloseButton>
          
          <ModalTitle>{t.assetModal.title}</ModalTitle>

          {loading && (
            <LoadingSpinner>
              <Spinner />
            </LoadingSpinner>
          )}

          {error && (
            <ErrorMessage>
              {error}
            </ErrorMessage>
          )}

          {assetDetails && (
            <>
              <AssetInfo>
                <InfoRow>
                  <InfoLabel>{t.assetModal.assetName}:</InfoLabel>
                  <InfoValue>{assetDetails['Asset Name'] || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.issuer}:</InfoLabel>
                  <InfoValue>{assetDetails.Issuer || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.standard}:</InfoLabel>
                  <InfoValue>{assetDetails.Standard || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.assetType}:</InfoLabel>
                  <InfoValue>{assetDetails['Asset Type'] || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.projectType}:</InfoLabel>
                  <InfoValue>{assetDetails['Project type'] || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.vintage}:</InfoLabel>
                  <InfoValue>{assetDetails.Vintage ?? na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.serialNumberBatch}:</InfoLabel>
                  <InfoValue>
                    {assetDetails['Serial Number Batch'] || na()}
                  </InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.tokenAddress}:</InfoLabel>
                  <InfoValue>{assetDetails['Token Address'] || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.txid}:</InfoLabel>
                  <InfoValue>{assetDetails.txid || na()}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.createdDate}:</InfoLabel>
                  <InfoValue>{formatDate(assetDetails['Created Date'])}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.modifiedDate}:</InfoLabel>
                  <InfoValue>{formatDate(assetDetails['Modified Date'])}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t.assetModal.createdBy}:</InfoLabel>
                  <InfoValue>{assetDetails['Created By'] || na()}</InfoValue>
                </InfoRow>

                {assetRegistryUrl ? (
                  <InfoRow>
                    <InfoLabel>{t.assetModal.registry}:</InfoLabel>
                    <InfoValueLink
                      href={assetRegistryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t.assetModal.viewOnRegistry}
                    >
                      {t.assetModal.viewOnRegistry}
                    </InfoValueLink>
                  </InfoRow>
                ) : null}
              </AssetInfo>

              <QuantityHighlight>
                <QuantityValue>
                  {formatNumber(
                    assetDetails["Quantity issued"] ?? assetDetails.Quantity
                  )}
                </QuantityValue>
                <QuantityLabel>{t.assetModal.creditsIssued}</QuantityLabel>
              </QuantityHighlight>

              <ActionButton onClick={handlePurchaseRequest}>
                {t.assetModal.purchaseButton}
              </ActionButton>
            </>
          )}
        </ModalContent>
      </ModalOverlay>

      <PurchaseRequestModal
        isOpen={isPurchaseModalOpen}
        onClose={closePurchaseModal}
        packageName={assetDetails?.['Asset Name'] || assetName}
      />
    </>
  );
};

export default AssetDetailsModal;
